import type { AxiosError } from 'axios';

import { captureException } from '@sentry/react';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * When the request left, so the report can say how long it waited. The one fact the
     * error itself cannot carry: a connection that was already dead answers instantly, an
     * upstream that stopped answering does not, and on screen the two are the same red box.
     */
    transportSentAt?: number;
  }
}

/**
 * A request that went out and got nothing back is the ONE API failure that says nothing
 * about the app, and since the toast for it went quiet on background reads
 * (`shouldToastApiError`) this is the only place it is recorded at all.
 *
 * What the tester could report was "an error on random screens" — true, and unanswerable:
 * the toast names no request, and nothing about it reached Sentry, so there was no way to
 * ask whether it was one endpoint, one connection, or one browser throwing away the tab's
 * requests. Everything here exists to answer that question and nothing else:
 *
 *   * `path` and `method` — WHICH request, as TAGS of one issue per kind of failure
 *     (`code` + `phase`). The fingerprint used to be per endpoint, so one phone losing its
 *     network minted an issue per route it had in flight — 31 issues from 48 events by
 *     2026-10-10, not one of them a bad route. "One route or every route" is the
 *     `transport.path` breakdown of that issue now — with resource ids replaced by `:id`,
 *     or one route would show up as a value per organization, user or workout.
 *   * `code` — Axios's own verdict. `ERR_NETWORK` is a connection that failed,
 *     `ECONNABORTED` a browser abort OR a configured timeout (Axios gives both the same
 *     code; `endSession` sets one), `ERR_CANCELED` something that aborted the request on
 *     purpose. Causes, not blame: the code alone does not convict the backend.
 *   * `visibility` and `online` — Sentry 6Q/6R. The backend answered that request with 200;
 *     the phone had already thrown the connection away because the tab went to the
 *     background. Without these the event accuses a server of something a browser did.
 *     A failure in a HIDDEN tab is still SENT (40 of those 48 events were one): this app
 *     does not refetch on focus (`refetchOnWindowFocus: false`), nothing replays a failed
 *     write, and callers such as `useRecordEngagement` skip their own report because this
 *     one exists. The tag is the page state WHEN THE FAILURE IS REPORTED, not over the
 *     request's lifetime — context for reading the issue, not proof of who caused it.
 *   * `durationMs` — instantly dead, or dead after a wait.
 *
 * Deliberately NOT carried, exactly as `reportHardSessionFailure` next door: the original
 * error, the request config, the headers. An `Authorization` header in a telemetry payload
 * is a leak, and the query string goes too — search boxes and e-mail addresses live there,
 * and the endpoint alone is the whole diagnostic value.
 */

/**
 * One event per burst key — code, phase, method and NORMALIZED route — per window. Later
 * failures on that key are only counted, and the count rides on the next event of the same
 * key; it is lost if none comes (or the map is cleared, or the page goes away).
 */
const COLLAPSE_WINDOW_MS = 30_000;

/**
 * How many burst keys are remembered. A bound on memory, not a rate limit: one route can
 * hold several keys (different codes/methods), and reaching the bound clears the map.
 */
const MAX_TRACKED_ENDPOINTS = 100;

const lastReported = new Map<string, { at: number; collapsed: number }>();

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A Twilio conversation SID — the id the chat routes carry instead of a UUID. */
const CONVERSATION_SID_SEGMENT = /^CH[0-9a-f]{32}$/i;

const isIdSegment = (segment: string) =>
  UUID_SEGMENT.test(segment) || CONVERSATION_SID_SEGMENT.test(segment);

/**
 * `/api` + `/v2/organizations/<uuid>/members?search=…` → `/api/v2/organizations/:id/members`.
 * The ROUTE, not the resource: whole UUID (and conversation SID) segments become `:id` so one
 * failing route is one tag value and one burst, not one per organization or conversation.
 * Unknown when the config never made it.
 */
function describePath(error: AxiosError) {
  const { baseURL = '', url = '' } = error.config ?? {};
  const path = `${baseURL}${url}`
    .split('?')[0]
    .split('/')
    .map((segment) => (isIdSegment(segment) ? ':id' : segment))
    .join('/');
  return path || 'unknown';
}

function describeMethod(error: AxiosError) {
  return error.config?.method?.toUpperCase() || 'unknown';
}

/** `visible`, `hidden`, or `unknown` where there is no document to ask. */
function describeVisibility() {
  return typeof document === 'undefined' ? 'unknown' : document.visibilityState;
}

/**
 * `navigator.onLine` answers "attached to a network", not "reached the other end" — which
 * is why `isConnectivityError` refuses to decide anything by it. As a FACT ON an event it
 * is still worth having: `false` settles the question outright.
 */
function describeOnline() {
  return typeof navigator === 'undefined' ||
    typeof navigator.onLine !== 'boolean'
    ? 'unknown'
    : String(navigator.onLine);
}

export function reportNetworkFailure(error: AxiosError) {
  // `request` present means it left and nothing came back; absent means it never left —
  // an interceptor refused it, or it was aborted before dispatch. Same sentence on screen,
  // different cause, so the event has to be able to tell them apart.
  const phase = error.request ? 'no-response' : 'setup';
  const method = describeMethod(error);
  const path = describePath(error);
  const code = error.code || 'unknown';
  const visibility = describeVisibility();
  const online = describeOnline();
  const now = Date.now();
  const sentAt = error.config?.transportSentAt;
  const durationMs = typeof sentAt === 'number' ? now - sentAt : undefined;

  // The burst key keeps the normalized route, so each route a burst hit still lands one
  // event in the issue — that is what the `transport.path` breakdown is made of.
  const key = [code, phase, method, path].join('|');
  const previous = lastReported.get(key);
  if (previous && now - previous.at < COLLAPSE_WINDOW_MS) {
    // Many requests in flight at once (a page's parallel reads, a reconnect refetch) and one
    // dead connection answers all of them: one finding, its size counted onto the next event.
    previous.collapsed += 1;
    return;
  }
  if (lastReported.size >= MAX_TRACKED_ENDPOINTS) lastReported.clear();
  lastReported.set(key, { at: now, collapsed: 0 });

  try {
    // The ROUTE stays in the exception value although the fingerprint groups by kind only.
    // The SDK's default `Dedupe` integration drops an event whose exception value,
    // fingerprint and stack frames equal the previous one's — tags are not compared — and
    // every error here is built at this one line. Without the route, a second route's
    // failure was dropped as a duplicate of the first, for as long as nothing else was
    // captured in between (review 2026-10-10, transport-grouping-and-chat-pagination #1).
    // The issue title therefore shows one route; the breakdown is the `transport.path` tag.
    captureException(
      new Error(`Transport failure (${phase}): ${code} ${method} ${path}`),
      {
        extra: {
          collapsed: previous?.collapsed ?? 0,
          durationMs,
          replay: error.config?._retry === true,
        },
        fingerprint: ['transport-failure', code, phase],
        level: 'warning',
        tags: {
          'transport.code': code,
          'transport.method': method,
          'transport.online': online,
          'transport.path': path,
          'transport.phase': phase,
          'transport.visibility': visibility,
        },
      },
    );
  } catch {
    // A reporter that throws inside an error handler replaces the API failure with one of
    // its own making, and the caller never gets the typed error it was about to throw.
  }
}
